import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import Countdown from "./Countdown";
import { openEnquiry } from "../lib/modal";

const ROWS = [
  { type: "Office Suite", area: "800 sq.ft", price: "₹2.64 Cr*" },
  { type: "Office Suite", area: "1,200 sq.ft", price: "₹3.96 Cr*", tag: "Most Preferred" },
  { type: "Half Floor", area: "22,500 sq.ft", price: "₹74.25 Cr*" },
  { type: "Full Floor", area: "46,000 sq.ft", price: "₹151.8 Cr*" },
];

const PLAN = [
  ["On Booking", "10%"],
  ["On Agreement (within 30 days)", "20%"],
  ["Construction-Linked Milestones", "60%"],
  ["On Possession", "10%"],
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 border-t border-border">
      <div className="max-w-screen-2xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] uppercase tracking-[0.35em] text-primary mb-5">Pre-Launch Pricing</p>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
            Own the district from <span className="italic text-primary">₹2.64 Cr*</span>
          </h2>
          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-12">
            ₹33,000 per sq.ft* · Carpet 800 – 46,000 sq.ft
          </p>
          <div data-testid="price-table">
            <div className="grid grid-cols-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground border-b border-border pb-4">
              <span>Configuration</span><span>Area</span><span className="text-right">Price Onwards</span>
            </div>
            {ROWS.map((r, i) => (
              <motion.div
                key={r.area}
                data-testid={`price-row-${r.area.replace(/[^0-9]/g, "")}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className={`relative grid grid-cols-3 items-center border-b py-6 hover:bg-muted/30 transition-colors px-2 -mx-2 ${r.tag ? "border-primary/40 bg-primary/5" : "border-border/60"}`}
              >
                <span className="text-sm">{r.type}</span>
                {r.tag && (
                  <span className="absolute -top-2.5 left-2 text-[8px] uppercase tracking-[0.2em] bg-primary text-primary-foreground px-2 py-0.5">{r.tag}</span>
                )}
                <span className="text-sm text-muted-foreground">{r.area}</span>
                <span className="font-display text-2xl md:text-3xl text-primary text-right">{r.price}</span>
              </motion.div>
            ))}
          </div>
          <button
            onClick={() => openEnquiry("Pre-Launch EOI")}
            data-testid="pricing-costing-button"
            className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-foreground hover:text-primary transition-colors group"
          >
            Get complete costing
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <div className="mt-10 border border-border p-6 bg-white">
            <Countdown />
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="border border-border p-8 md:p-10 h-full flex flex-col">
            <p className="text-[11px] uppercase tracking-[0.35em] text-primary mb-8">Payment Plan</p>
            {PLAN.map(([label, pct]) => (
              <div key={label} className="flex items-baseline justify-between border-b border-border/60 py-4">
                <span className="text-sm text-muted-foreground">{label}</span>
                <span className="font-display text-2xl text-foreground">{pct}</span>
              </div>
            ))}
            <div className="mt-8 bg-muted/40 border border-border/60 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Expression of Interest</p>
              <p className="text-sm leading-relaxed">
                EOI of <span className="text-primary">₹4,50,000</span> secures priority allocation at pre-launch pricing — fully refundable during the EOI window.*
              </p>
            </div>
            <p className="text-[11px] text-muted-foreground/70 leading-relaxed mt-6">
              *Indicative pre-launch prices, subject to change. Taxes, stamp duty & registration extra. Thane commercial capital values have appreciated strongly year-on-year.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
